import { NavLink } from 'react-router-dom';
import { Home, KeyRound, CalendarRange, LayoutGrid, CircleUser, MoreHorizontal } from 'lucide-react';
import { useExperienceStore } from '@shared/stores/experienceStore';
import { cn } from '@shared/lib/cn';
import type { CompiledNavigationItem } from '@shared/types/domainConfig';
import { PanicSosBadge } from '@features/panic/PanicSosBadge';

const MAX_TABS = 5;

/**
 * Bottom tab bar. Uses `experienceStore.bottom_tabs` from
 * CompiledMemberAppExperience when present (data-driven per tenant);
 * falls back to the Fancourt 5-tab set so the shell still renders if the
 * domain-config call hasn't returned.
 *
 * Fallback tabs: Home · Visitors · Book · Estate · Me.
 *
 * Active state is a soft pill that wraps the whole tab content (icon +
 * label) in a tenant-primary tint. Inactive tabs render the icon + label
 * in muted ink with no background.
 */

const FALLBACK_TABS: Array<{ system_key: string; label: string; route: string; icon: typeof Home }> = [
  { system_key: 'home', label: 'Home', route: '/app/home', icon: Home },
  { system_key: 'visitors', label: 'Visitors', route: '/app/access-invites', icon: KeyRound },
  { system_key: 'book', label: 'Book', route: '/app/facilities', icon: CalendarRange },
  { system_key: 'estate', label: 'Estate', route: '/app/more', icon: LayoutGrid },
  { system_key: 'me', label: 'Me', route: '/app/profile', icon: CircleUser },
];

export function TabBar(): JSX.Element {
  const tabs = useExperienceStore((s) => s.getBottomTabs());
  /** `experienceStore` normalizes bottom_tabs (Home first); fallback when config not loaded. */
  const items = (tabs.length > 0 ? tabs : FALLBACK_TABS).slice(0, MAX_TABS);

  return (
    <nav className="absolute inset-x-0 bottom-0 z-50 hairline-t bg-paper pb-[max(0.5rem,calc(env(safe-area-inset-bottom)-0.75rem))]">
      {/* Flex row so the bar fills the screen and items share space evenly
          regardless of how many tabs the tenant has configured (3, 4 or 5). */}
      <ul className="flex">
        {items.map((item) => (
          <li key={(item as CompiledNavigationItem).id ?? item.system_key} className="flex flex-1">
            <NavLink to={routeFor(item)} className="flex flex-1 items-center justify-center px-1 py-2">
              {({ isActive }) =>
                isPanicTab(item) ? (
                  // Emergency tab: white SOS on a red disc, no icon/label —
                  // but keep the shared active pill so the selected state reads.
                  <div
                    className={cn(
                      'flex w-full flex-col items-center justify-center rounded-lg px-2 py-1 transition-colors duration-fast ease-smooth',
                      isActive ? 'bg-primary-50' : '',
                    )}
                  >
                    <PanicSosBadge className="h-9 w-9 text-[0.7rem]" />
                  </div>
                ) : (
                  <div
                    className={cn(
                      'flex w-full flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 transition-colors duration-fast ease-smooth',
                      isActive ? 'bg-primary-50 text-primary-700' : 'text-ink-muted',
                    )}
                  >
                    <TabIcon item={item} />
                    <span
                      className={cn(
                        'text-caption transition-colors duration-fast',
                        isActive && 'font-medium',
                      )}
                    >
                      {labelFor(item)}
                    </span>
                  </div>
                )
              }
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function routeFor(item: CompiledNavigationItem | (typeof FALLBACK_TABS)[number]): string {
  if ('route' in item && typeof item.route === 'string') {
    return item.route.startsWith('/') ? item.route : `/app/${item.route}`;
  }
  return '/app/home';
}

function labelFor(item: CompiledNavigationItem | (typeof FALLBACK_TABS)[number]): string {
  return item.label || 'Tab';
}

/** The Panic tab is rendered as an SOS badge instead of icon + label. */
function isPanicTab(item: CompiledNavigationItem | (typeof FALLBACK_TABS)[number]): boolean {
  const systemKey = (item.system_key ?? '').trim().toLowerCase();
  const moduleKey = ('module_required' in item ? item.module_required ?? '' : '').toLowerCase();
  return systemKey === 'panic' || moduleKey === 'panic' || routeFor(item) === '/app/panic';
}

const TAB_ICON_BY_SYSTEM_KEY: Record<string, typeof Home> = {
  home: Home,
  visitors: KeyRound,
  book: CalendarRange,
  estate: LayoutGrid,
  more: LayoutGrid,
  me: CircleUser,
};

function TabIcon({ item }: { item: CompiledNavigationItem | (typeof FALLBACK_TABS)[number] }): JSX.Element {
  if ('icon' in item) {
    const Icon = item.icon;
    return <Icon className="h-5 w-5" strokeWidth={1.75} />;
  }

  const systemKey = (item.system_key ?? '').trim().toLowerCase();
  const LucideIcon = TAB_ICON_BY_SYSTEM_KEY[systemKey];
  const iconUrl = item.icon_url?.trim();

  if (systemKey === 'home') {
    return <Home className="h-5 w-5" strokeWidth={1.75} />;
  }
  if (iconUrl) {
    return <img src={iconUrl} alt="" className="h-5 w-5 object-contain" aria-hidden />;
  }
  if (LucideIcon) {
    return <LucideIcon className="h-5 w-5" strokeWidth={1.75} />;
  }
  return <MoreHorizontal className="h-5 w-5" strokeWidth={1.75} />;
}
